import { StickyNote, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/dialog";
import AnalystNotesPanel from "../analystNotes/AnalystNotesPanel";
import FabButton from "./FabButton";

export const ANALYST_NOTES_PANEL_ID = "analyst-notes-panel";

type AnalystNotesModalProperties = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The deal / approval whose notes the modal reads and writes. */
  approvalId: string;
  /** Dims the FAB and blocks opening — used when the role's actions couldn't be loaded. */
  disabled?: boolean;
  /** Label/tooltip shown in place of the default one while `disabled`. */
  disabledLabel?: string;
};

/**
 * Amber "Analyst Notes" FAB + modal (OR-22625). The FAB toggles a controlled
 * Radix Dialog listing the deal's analyst notes and an add-note form (see
 * {@link AnalystNotesPanel}). Parallels `InternalAuditDrawer` — the FAB and
 * the Radix content are separate concerns; the button drives open state through
 * the cluster's `onOpenChange` rather than acting as a DialogTrigger.
 */
function AnalystNotesModal({ open, onOpenChange, approvalId, disabled, disabledLabel }: AnalystNotesModalProperties) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FabButton
        label={disabled && disabledLabel ? disabledLabel : t("actions.analystNotes")}
        icon={<StickyNote className="size-6" />}
        variant="amber"
        expanded={open}
        controls={ANALYST_NOTES_PANEL_ID}
        hasPopup="dialog"
        disabled={disabled}
        onClick={() => onOpenChange(!open)}
      />
      <DialogContent
        id={ANALYST_NOTES_PANEL_ID}
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 sm:max-w-3xl"
      >
        {/* Header — navy bar with title + close. */}
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">{t("analystNotes.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("analystNotes.description")}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors"
            aria-label={t("analystNotes.close")}
          >
            <X className="size-4.5" aria-hidden="true" />
          </DialogClose>
        </div>

        <AnalystNotesPanel approvalId={approvalId} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

export default AnalystNotesModal;
